import { ReactNode, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/providers/AuthProvider';
import { Loader2 } from 'lucide-react';
import {
  AUTH_TOKEN_KEY,
  AUTH_USER_KEY,
  SESSION_ACCESS_KEY,
  SESSION_REFRESH_KEY,
} from '@/config/constants';

interface CoreGuardProps {
  children: ReactNode;
}

/**
 * CoreGuard - Main authentication guard for the application
 * Handles automatic redirection based on authentication status
 * - If user has valid token, redirect to dashboard (from login page)
 * - If user has no token, redirect to login (from any protected page)
 */
export default function CoreGuard({ children }: CoreGuardProps) {
  const { isAuthenticated, isLoading, tokens } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const isLoginPage = location.pathname === '/login';

  useEffect(() => {
    // Don't redirect during initial loading
    if (isLoading) return;

    // If user is authenticated and on login page, redirect to dashboard
    if (isAuthenticated && isLoginPage) {
      navigate('/dashboard', { replace: true });
      return;
    }

    // If user is not authenticated and not on login page, redirect to login
    if (!isAuthenticated && !isLoginPage) {
      navigate('/login', { replace: true });
      return;
    }
  }, [isAuthenticated, isLoading, isLoginPage, navigate]);

  // Additional effect to check token validity
  useEffect(() => {
    if (!isLoading && !isLoginPage) {
      // Check if we have authentication state but missing tokens
      if (isAuthenticated && !tokens?.access) {
        console.warn(
          'Authentication state inconsistent - missing access token'
        );
        navigate('/login', { replace: true });
        return;
      }

      // Check for empty/invalid tokens in storage
      const storedTokens = localStorage.getItem(AUTH_TOKEN_KEY);
      const storedUser = localStorage.getItem(AUTH_USER_KEY);

      if (!isAuthenticated && (storedTokens || storedUser)) {
        // Clean up inconsistent storage state
        localStorage.removeItem(AUTH_TOKEN_KEY);
        localStorage.removeItem(AUTH_USER_KEY);
        sessionStorage.removeItem(SESSION_ACCESS_KEY);
        sessionStorage.removeItem(SESSION_REFRESH_KEY);
      }
    }
  }, [isAuthenticated, isLoading, isLoginPage, navigate, tokens]);

  // Show loading spinner during authentication check
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="h-8 w-8 animate-spin text-primary" />
          <p className="text-sm text-muted-foreground">
            Checking authentication...
          </p>
        </div>
      </div>
    );
  }

  return <>{children}</>;
}
